import {CUSTOMER_PACKAGE_TYPES} from 'types/gtins';
import {TransferDocumentLine} from 'types/transfer-documents';
import {CodeResponseType, CodeResponseTypeInfo} from 'types/validation';

type TransportCodeInfo = CodeResponseType['info'] | CodeResponseTypeInfo;

const normalizeMarkingCode = (code: string) =>
    code
        .replace(/\s/g, '')
        .split(String.fromCharCode(29))
        .join('');

const getChildCodes = (info: TransportCodeInfo, parentCodes = new Set<string>()) => {
    const excludedCodes = new Set(parentCodes).add(normalizeMarkingCode(info.identityCode));
    const uniqueCodes = new Set<string>();

    return (info.childrenMarkingCodes ?? []).filter((child) => {
        const identityCode = normalizeMarkingCode(child.identityCode);
        if (excludedCodes.has(identityCode) || uniqueCodes.has(identityCode)) {
            return false;
        }
        uniqueCodes.add(identityCode);
        return true;
    });
};

export const isTransportLine = (line: Pick<TransferDocumentLine, 'packagingType'>) =>
    Boolean(line.packagingType && !CUSTOMER_PACKAGE_TYPES.includes(line.packagingType));

export const collectTransferDocumentCodes = (lines: TransferDocumentLine[]): string[] =>
    lines.flatMap((line) => [
        line.markingCode,
        ...collectTransferDocumentCodes(line.subRows ?? []),
    ]);

export const countTransferDocumentCodes = (lines: TransferDocumentLine[]): number =>
    collectTransferDocumentCodes(lines).length;

const collectChildCodes = (info: TransportCodeInfo, parentCodes = new Set<string>()): string[] => {
    const nextParentCodes = new Set(parentCodes).add(normalizeMarkingCode(info.identityCode));
    return getChildCodes(info, parentCodes).flatMap((child) => [
        child.identityCode,
        ...collectChildCodes(child, nextParentCodes),
    ]);
};

const collectCodeInfo = (
    info: TransportCodeInfo,
    parentCodes = new Set<string>(),
): TransportCodeInfo[] => {
    const nextParentCodes = new Set(parentCodes).add(normalizeMarkingCode(info.identityCode));
    return [
        info,
        ...getChildCodes(info, parentCodes).flatMap((child) =>
            collectCodeInfo(child, nextParentCodes),
        ),
    ];
};

const toTransferDocumentLine = (
    info: TransportCodeInfo,
    storedLines: Map<string, TransferDocumentLine>,
    parentCodes = new Set<string>(),
): TransferDocumentLine => {
    const storedLine = storedLines.get(normalizeMarkingCode(info.identityCode));
    const isTransport = !CUSTOMER_PACKAGE_TYPES.includes(info.packagingType);
    const productCode = 'productCode' in info ? info.productCode : undefined;
    const children = getChildCodes(info, parentCodes);
    const nextParentCodes = new Set(parentCodes).add(normalizeMarkingCode(info.identityCode));

    return {
        lineNumber: storedLine?.lineNumber ?? 0,
        lineAmount: isTransport ? 0 : storedLine?.lineAmount ?? '',
        vat: isTransport ? 0 : storedLine?.vat ?? '',
        lineTaxAmount: isTransport ? 0 : storedLine?.lineTaxAmount ?? '',
        item: storedLine?.item || info.identityCode,
        productName: storedLine?.productName || info.productName || '',
        productCode: storedLine?.productCode || productCode || info.gtin || '',
        markingCode: info.identityCode,
        packagingType: info.packagingType,
        ...(children.length && {
            subRows: children.map((child) =>
                toTransferDocumentLine(child, storedLines, nextParentCodes),
            ),
        }),
    };
};

export const createTransferDocumentLine = (
    info: TransportCodeInfo,
    lineAmount: number | '' = '',
    vat: number | '' = '',
): TransferDocumentLine => {
    const line = toTransferDocumentLine(info, new Map());

    if (!isTransportLine(line)) {
        line.lineAmount = lineAmount;
        line.vat = vat;
        line.lineTaxAmount =
            lineAmount === '' || vat === ''
                ? ''
                : Math.round(((Number(lineAmount) * Number(vat)) / 100) * 100) / 100;
    }

    return line;
};

export const restoreTransferDocumentLines = (
    lines: TransferDocumentLine[],
    validatedCodes: TransportCodeInfo[],
): TransferDocumentLine[] => {
    const hasStoredHierarchy = lines.some((line) => Boolean(line.parent) || line.level != null);
    if (hasStoredHierarchy) {
        const codeInfo = new Map(
            validatedCodes
                .flatMap((info) => collectCodeInfo(info))
                .map((info) => [normalizeMarkingCode(info.identityCode), info]),
        );
        const nodes = new Map<string, TransferDocumentLine>(
            lines.map((line): [string, TransferDocumentLine] => {
                const normalizedCode = normalizeMarkingCode(line.markingCode);
                const info = codeInfo.get(normalizedCode);
                return [
                    normalizedCode,
                    {
                        ...line,
                        packagingType: info?.packagingType ?? line.packagingType,
                        productName: line.productName || info?.productName || '',
                        productCode: line.productCode || info?.gtin || '',
                        subRows: [],
                    },
                ];
            }),
        );
        const roots: TransferDocumentLine[] = [];

        lines.forEach((line) => {
            const node = nodes.get(normalizeMarkingCode(line.markingCode));
            if (!node) return;
            const parent = line.parent ? nodes.get(normalizeMarkingCode(line.parent)) : undefined;
            if (parent && parent.markingCode !== node.markingCode) {
                parent.subRows?.push(node);
            } else {
                roots.push(node);
            }
        });

        return roots;
    }

    const storedLines = new Map(
        collectTransferDocumentLinesWithData(lines).map((line) => [
            normalizeMarkingCode(line.markingCode),
            line,
        ]),
    );
    const uniqueValidatedCodes = Array.from(
        new Map<string, TransportCodeInfo>(
            validatedCodes.map((info) => [normalizeMarkingCode(info.identityCode), info]),
        ).values(),
    );
    const nestedCodes = new Set(
        uniqueValidatedCodes.flatMap((info) => collectChildCodes(info)).map(normalizeMarkingCode),
    );
    const roots = uniqueValidatedCodes.filter(
        (info) => !nestedCodes.has(normalizeMarkingCode(info.identityCode)),
    );
    const restoredCodes = new Set(
        roots
            .flatMap((root) => [root.identityCode, ...collectChildCodes(root)])
            .map(normalizeMarkingCode),
    );

    return [
        ...roots.map((root) => toTransferDocumentLine(root, storedLines)),
        ...lines.filter((line) => !restoredCodes.has(normalizeMarkingCode(line.markingCode))),
    ];
};

export const collectTransferDocumentLinesWithData = (
    lines: TransferDocumentLine[],
): TransferDocumentLine[] =>
    lines.flatMap((line) => [line, ...collectTransferDocumentLinesWithData(line.subRows ?? [])]);

export const flattenTransferDocumentLines = (
    lines: TransferDocumentLine[],
): TransferDocumentLine[] => {
    const result: TransferDocumentLine[] = [];

    const appendLines = (
        nestedLines: TransferDocumentLine[],
        parent: string | undefined,
        level: number,
    ) => {
        nestedLines.forEach((line) => {
            result.push({
                lineNumber: result.length + 1,
                lineAmount: Number(line.lineAmount),
                vat: Number(line.vat),
                lineTaxAmount: Number(line.lineTaxAmount),
                item: line.item,
                productName: line.productName,
                productCode: line.productCode,
                markingCode: line.markingCode,
                ...(parent && {parent}),
                level,
            });
            appendLines(line.subRows ?? [], line.markingCode, level + 1);
        });
    };

    appendLines(lines, undefined, 1);
    return result;
};

export const updateTransferDocumentLine = (
    lines: TransferDocumentLine[],
    markingCode: string,
    update: (line: TransferDocumentLine) => TransferDocumentLine,
): TransferDocumentLine[] =>
    lines.map((line) =>
        line.markingCode === markingCode
            ? update(line)
            : {
                  ...line,
                  subRows: updateTransferDocumentLine(line.subRows ?? [], markingCode, update),
              },
    );

export const copyTransferDocumentLineData = (
    lines: TransferDocumentLine[],
    markingCode: string,
): TransferDocumentLine[] => {
    const copyWithinSiblings = (
        siblings: TransferDocumentLine[],
    ): {lines: TransferDocumentLine[]; copied: boolean} => {
        const source = siblings.find((line) => line.markingCode === markingCode);
        if (source) {
            return {
                copied: true,
                lines: siblings.map((line) =>
                    !isTransportLine(line) && line.productCode === source.productCode
                        ? {
                              ...line,
                              lineAmount: source.lineAmount,
                              vat: source.vat,
                              lineTaxAmount: source.lineTaxAmount,
                          }
                        : line,
                ),
            };
        }

        let copied = false;
        const updatedLines = siblings.map((line) => {
            if (copied || !line.subRows?.length) return line;
            const result = copyWithinSiblings(line.subRows);
            copied = result.copied;
            return result.copied ? {...line, subRows: result.lines} : line;
        });

        return {lines: updatedLines, copied};
    };

    return copyWithinSiblings(lines).lines;
};

export const areTransferDocumentLinesValid = (lines: TransferDocumentLine[]): boolean =>
    lines.every((line) => {
        if (isTransportLine(line)) {
            return areTransferDocumentLinesValid(line.subRows ?? []);
        }

        return (
            Number(line.lineAmount) > 0 &&
            line.vat !== '' &&
            !Number.isNaN(Number(line.vat)) &&
            Number(line.vat) >= 0
        );
    });
